import { api } from "@/lib/api";

// §105 Shared contact list (LinkedIn connections, Gmail/Outlook contacts) for the team or client workspace.
export const CN = "/admin/connections";

// Our fields + header synonyms (normalized: lowercase, letters/digits only).
export const FIELDS = [
  ["full_name", "Full name", ["name", "fullname", "displayname", "contactname"]],
  ["first_name", "First name", ["firstname", "givenname", "first", "fname"]],
  ["middle_name", "Middle name", ["middlename", "additionalname"]],
  ["last_name", "Last name", ["lastname", "familyname", "surname", "last", "lname"]],
  ["email", "Email", ["emailaddress", "email", "email1value", "primaryemail", "workemail", "businessemail", "email1", "emailaddress1", "personalemail", "homeemail", "email2value", "email3value", "email2", "email3", "otheremail", "email2address", "email3address", "emailaddress2", "emailaddress3"]],
  ["phone", "Phone", ["phone", "phonenumber", "mobile", "mobilephone", "cell", "cellphone", "businessphone", "workphone", "homephone", "primaryphone", "phone1value", "phone2value", "phone3value", "otherphone", "telephone", "tel", "directdial", "companyphone", "businessphone2", "homephone2", "carphone", "pager"]],
  ["company", "Company", ["company", "organization", "organization1name", "organizationname", "companyname", "employer", "currentcompany", "account", "accountname"]],
  ["title", "Job title", ["position", "title", "jobtitle", "organization1title", "currenttitle", "role", "headline", "occupation"]],
  ["department", "Department", ["department", "organization1department", "dept", "team"]],
  ["linkedin_url", "LinkedIn profile", ["url", "profileurl", "linkedinurl", "linkedin", "linkedinprofile", "linkedinprofileurl", "personlinkedinurl", "li"]],
  ["github_url", "GitHub", ["github", "githuburl", "githubprofile"]],
  ["twitter_url", "Twitter / X", ["twitter", "twitterurl", "x", "xurl", "twitterhandle"]],
  ["website", "Website", ["website", "web", "webpage", "homepage", "personalwebsite", "website1value", "blog", "url2", "website2value"]],
  ["portfolio_url", "Portfolio", ["portfolio", "portfoliourl", "dribbble", "behance"]],
  ["other_url", "Other link", ["link", "links", "otherurl", "sociallink", "profile", "stackoverflow", "medium", "facebook", "instagram"]],
  ["company_domain", "Company website / domain", ["companydomain", "domain", "companywebsite", "companyurl", "organizationwebsite"]],
  ["location", "Location", ["location", "address", "fulladdress", "homeaddress", "businessaddress", "address1formatted", "mailingaddress"]],
  ["city", "City", ["city", "homecity", "businesscity", "address1city", "town"]],
  ["state", "State / region", ["state", "region", "province", "homestate", "businessstate", "address1region", "stateprovince"]],
  ["country", "Country", ["country", "homecountry", "businesscountry", "address1country", "countryregion"]],
  ["postal_code", "Zip / postal code", ["zip", "zipcode", "postalcode", "postcode", "homepostalcode", "businesspostalcode", "address1postalcode"]],
  ["connected_on", "Connected on", ["connectedon", "connectiondate", "dateconnected", "createdat", "dateadded"]],
  ["notes", "Notes", ["notes", "note", "comments", "description", "bio", "summary"]],
  ["tags", "Tags / labels", ["tags", "labels", "groupmembership", "categories", "lists"]],
];
export const FIELD_LABEL = Object.fromEntries(FIELDS.map(([k, l]) => [k, l]));
const MULTI = new Set(["email", "phone", "linkedin_url", "github_url", "twitter_url", "website", "portfolio_url", "other_url", "tags"]);

export const SOURCES = [["linkedin", "LinkedIn connections"], ["google", "Gmail / Google contacts"], ["outlook", "Outlook contacts"], ["csv", "Other CSV"]];

// CSV with quoted fields (incl. commas/newlines inside quotes); auto-detects ; or tab separators.
const parseCsv = (text) => {
  const first = text.split(/\r?\n/, 1)[0] || "";
  const sep = [",", ";", "\t"].sort((a, b) => first.split(b).length - first.split(a).length)[0];
  const rows = []; let row = []; let cell = ""; let q = false;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (q) { if (ch === '"') { if (text[i + 1] === '"') { cell += '"'; i += 1; } else q = false; } else cell += ch; continue; }
    if (ch === '"') q = true;
    else if (ch === sep) { row.push(cell); cell = ""; }
    else if (ch === "\n" || ch === "\r") { if (ch === "\r" && text[i + 1] === "\n") i += 1; row.push(cell); rows.push(row); row = []; cell = ""; }
    else cell += ch;
  }
  if (cell || row.length) { row.push(cell); rows.push(row); }
  return rows.filter((r) => r.some((c) => c.trim()));
};

const norm = (h) => h.toLowerCase().replace(/[^a-z0-9]/g, "");
export const linkKind = (u) => {
  const s = u.toLowerCase();
  if (s.includes("linkedin.com")) return "linkedin_url";
  if (s.includes("github.com")) return "github_url";
  if (s.includes("twitter.com") || /(^|\/\/)(www\.)?x\.com/.test(s)) return "twitter_url";
  if (/dribbble|behance|portfolio/.test(s)) return "portfolio_url";
  if (/stackoverflow|medium\.com|facebook|instagram|kaggle|gitlab/.test(s)) return "other_url";
  return "website";
};
const isUrl = (v) => /^(https?:\/\/|www\.)\S+$/i.test(v) || /^[\w-]+(\.[\w-]+)+\/\S*$/.test(v);
const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
const isPhone = (v) => /^\+?[\d\s().-]{7,20}$/.test(v) && (v.match(/\d/g) || []).length >= 7;

// Header synonyms first, then sniff sample values (URLs, emails, phones).
const guess = (h, samples) => {
  const n = norm(h);
  const exact = FIELDS.find(([, , syn]) => syn.includes(n));
  if (!exact && /(type|label|kind)$/.test(n)) return "";
  const hit = exact || FIELDS.find(([k, , syn]) => n.length > 3 && syn.some((s) => s.length > 3 && (n.includes(s) || s.includes(n))) && k !== "full_name");
  const vals = samples.filter(Boolean);
  if (vals.length) {
    if (vals.every(isEmail)) return "email";
    if (vals.every(isUrl)) { const kinds = vals.map(linkKind); return kinds.every((k) => k === kinds[0]) ? kinds[0] : "other_url"; }
    if (!hit && vals.every(isPhone)) return "phone";
  }
  if (/type|label|kind|primary$/.test(n) && !hit) return "";
  return hit ? hit[0] : "extra";
};

export const detectSource = (head) => {
  const h = head.map((x) => x.toLowerCase().trim());
  if (h.includes("connected on")) return "linkedin";
  if (h.includes("given name") || h.some((x) => x.startsWith("e-mail 1 - value"))) return "google";
  if (h.includes("e-mail address") || h.includes("business phone")) return "outlook";
  return "csv";
};

export const parseContacts = (text) => {
  const all = parseCsv(text.replace(/^\uFEFF/, ""));
  const hi = all.findIndex((r) => r.filter((c) => c.trim()).length >= 2 && r.some((c) => /(name|e-?mail|first|last|company|phone|url|linkedin)/i.test(c)));
  if (hi < 0) return { rows: [], data: [], head: [], mapping: [], source: "csv" };
  const head = all[hi].map((h) => h.trim());
  const data = all.slice(hi + 1);
  const mapping = head.map((h, i) => (h ? guess(h, data.slice(0, 25).map((r) => (r[i] || "").trim())) : ""));
  return { head, data, mapping, source: detectSource(head), rows: buildRows(data, head, mapping) };
};

// Turn raw rows into our contact shape using the (user-checked) column mapping.
export const buildRows = (data, head, mapping) => data.map((r) => {
  const o = {}; const multi = {}; const extra = {};
  mapping.forEach((k, i) => {
    const v = (r[i] || "").trim();
    if (!v || !k) return;
    if (k === "extra") { extra[head[i]] = v; return; }
    if (MULTI.has(k)) {
      v.split(/\s*[;|]\s*|\s+:::\s+/).filter(Boolean).forEach((x) => {
        const kk = ["website", "other_url"].includes(k) && isUrl(x) ? linkKind(x) : k;
        (multi[kk] = multi[kk] || []).push(x);
      });
    } else if (!o[k]) o[k] = v;
  });
  const uniq = (a) => [...new Set(a || [])];
  const emails = uniq(multi.email).filter(isEmail);
  const phones = uniq(multi.phone);
  const links = ["linkedin_url", "github_url", "twitter_url", "website", "portfolio_url", "other_url"].flatMap((k) => uniq(multi[k]).map((url) => ({ url: /^https?:\/\//i.test(url) ? url : `https://${url}`, kind: k.replace("_url", "") })));
  links.forEach((l) => { const k = `${l.kind === "website" ? "website" : `${l.kind}_url`}`; if (!o[k]) o[k] = l.url; });
  if (!o.full_name) o.full_name = [o.first_name, o.middle_name, o.last_name].filter(Boolean).join(" ");
  if (!o.location) o.location = [o.city, o.state, o.country].filter(Boolean).join(", ") || undefined;
  if (emails.length) { o.email = emails[0]; o.emails = emails; }
  if (phones.length) { o.phone = phones[0]; o.phones = phones; }
  if (links.length) o.links = links;
  if (multi.tags) o.tags = uniq(multi.tags.flatMap((t) => t.split(/\s*,\s*/))).filter((t) => !/^\* ?myContacts$/i.test(t));
  if (Object.keys(extra).length) o.extra = extra;
  return Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined && v !== ""));
}).filter((o) => o.full_name || o.email || o.linkedin_url);

export const importContacts = async (rows, source, fileName, onProgress) => {
  const tot = { imported: 0, updated: 0, skipped: 0, companies: 0, decision_makers: 0, candidates: 0 };
  const batch = `${Date.now()}`;
  for (let i = 0; i < rows.length; i += 500) {
    const d = (await api.post(`${CN}/import`, { source, file_name: fileName, batch_id: batch, rows: rows.slice(i, i + 500), final: i + 500 >= rows.length })).data || {};
    ["imported", "updated", "skipped"].forEach((k) => { tot[k] += d[k] || 0; });
    ["companies", "decision_makers", "candidates"].forEach((k) => { tot[k] += d.matched?.[k] || 0; });
    onProgress(Math.min(rows.length, i + 500));
  }
  return tot;
};

// §106 Saved column mappings (team/workspace-shared), matched by the file's header set.
export const sigOf = (head) => head.map(norm).filter(Boolean).sort().join("|");
export const loadMappings = () => api.get(`${CN}/mappings`).then((r) => r.data?.items || []).catch(() => []);
export const findMapping = (head, saved) => {
  const mine = new Set(head.map(norm).filter(Boolean));
  const score = (m) => { const hs = (m.headers || []).map(norm); const hit = hs.filter((h) => mine.has(h)).length; return hit / Math.max(mine.size, hs.length || 1); };
  return saved.map((m) => [m, m.signature === sigOf(head) ? 1 : score(m)]).filter(([, s]) => s >= 0.8).sort((a, b) => b[1] - a[1])[0]?.[0] || null;
};
export const applyMapping = (head, base, m) => { const nm = Object.fromEntries(Object.entries(m.mapping || {}).map(([k, v]) => [norm(k), v])); return head.map((h, i) => (norm(h) in nm ? nm[norm(h)] : base[i])); };
export const saveMapping = (name, head, mapping, source) => api.post(`${CN}/mappings`, { name, source, signature: sigOf(head), headers: head, mapping: Object.fromEntries(head.map((h, i) => [h, mapping[i]])) });
